import React, { useEffect, useState } from "react";
import {
    Users as UsersIcon,
    Plus,
    Loader2,
    Trash2,
    Pencil,
    X,
    KeyRound,
    ShieldCheck,
    CircleDot,
    Circle,
} from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const AGENTS = [
    { id: "priya", name: "Priya", hint: "Guest concierge" },
    { id: "tara", name: "Tara", hint: "HR & employee" },
];
const ACCESS_PRESETS = [
    { key: "both", label: "Both agents", value: ["priya", "tara"] },
    { key: "priya", label: "Only Priya", value: ["priya"] },
    { key: "tara", label: "Only Tara", value: ["tara"] },
];

const emptyForm = { name: "", email: "", password: "", role: "member", agent_access: ["tara"] };

const accentFor = (id) => (id === "tara" ? "#0F766E" : "#7E2A6C");

export default function SettingsUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [pwUser, setPwUser] = useState(null);
    const [pw, setPw] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/api/admin/users");
            setUsers(data.items || []);
        } catch (e) {
            if (e.response?.status === 403) setForbidden(true);
            else toast.error("Could not load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setForm(emptyForm);
        setEditingId(null);
        setShowForm(true);
    };

    const openEdit = (u) => {
        setForm({
            name: u.name || "",
            email: u.email,
            password: "",
            role: u.role,
            agent_access: u.role === "owner" ? ["priya", "tara"] : u.agent_access || [],
        });
        setEditingId(u.id);
        setShowForm(true);
    };

    const setAccessPreset = (value) => setForm((f) => ({ ...f, agent_access: value }));
    const presetKey = (access) => {
        const s = [...(access || [])].sort().join(",");
        if (s === "priya,tara") return "both";
        if (s === "priya") return "priya";
        if (s === "tara") return "tara";
        return "";
    };

    const save = async () => {
        if (!editingId) {
            if (!form.email.trim() || !form.password.trim()) {
                toast.error("Email and password are required");
                return;
            }
        }
        setSaving(true);
        try {
            if (editingId) {
                await adminApi.patch(`/api/admin/users/${editingId}`, {
                    name: form.name,
                    role: form.role,
                    agent_access: form.agent_access,
                });
                toast.success("User updated");
            } else {
                await adminApi.post("/api/admin/users", {
                    name: form.name,
                    email: form.email,
                    password: form.password,
                    role: form.role,
                    agent_access: form.agent_access,
                });
                toast.success("User created");
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (u) => {
        try {
            await adminApi.patch(`/api/admin/users/${u.id}`, { active: !u.active });
            toast.success(u.active ? "User disabled" : "User enabled");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update");
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
        try {
            await adminApi.delete(`/api/admin/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not delete");
        }
    };

    const resetPassword = async () => {
        if (pw.trim().length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        try {
            await adminApi.post(`/api/admin/users/${pwUser.id}/reset-password`, { password: pw.trim() });
            toast.success("Password reset");
            setPwUser(null);
            setPw("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not reset");
        }
    };

    if (forbidden) {
        return (
            <div className="p-10 max-w-3xl" data-testid="page-users-forbidden">
                <PageHeader eyebrow="Team" title="Users" testid="page-users-header" />
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-5 text-sm text-amber-800">
                    Only <strong>owners</strong> can manage users. Ask an owner if you need access.
                </div>
            </div>
        );
    }

    return (
        <div className="p-10 max-w-5xl" data-testid="page-users">
            <div className="flex items-start justify-between gap-4">
                <PageHeader
                    eyebrow="Team"
                    title="Users"
                    testid="page-users-header"
                />
                <button
                    onClick={openCreate}
                    data-testid="user-add-btn"
                    className="mt-2 inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2.5 rounded-md"
                >
                    <Plus size={15} /> Add user
                </button>
            </div>
            <p className="text-stone-600 -mt-4 mb-6 max-w-3xl">
                Create logins for your team and choose which chatbot(s) each person can work
                on. <strong>Owners</strong> can manage everything; <strong>members</strong> only see
                and act on their assigned agent(s).
            </p>

            {loading ? (
                <div className="flex items-center gap-2 text-stone-500 text-sm">
                    <Loader2 className="animate-spin" size={16} /> Loading…
                </div>
            ) : (
                <div className="border border-stone-200 rounded-xl overflow-hidden bg-white" data-testid="users-table">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">User</th>
                                <th className="text-left font-medium px-4 py-3">Role</th>
                                <th className="text-left font-medium px-4 py-3">Agent access</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="text-right font-medium px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {users.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-stone-50/60">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-stone-900">{u.name || "—"}</div>
                                        <div className="text-stone-500 text-xs">{u.email}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {u.role === "owner" ? (
                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-orchid-700 bg-orchid-700/10 px-2 py-1 rounded-full">
                                                <ShieldCheck size={12} /> Owner
                                            </span>
                                        ) : (
                                            <span className="text-xs font-medium text-stone-600 bg-stone-100 px-2 py-1 rounded-full">
                                                Member
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {(u.role === "owner" ? ["priya", "tara"] : u.agent_access || []).map((a) => (
                                                <span
                                                    key={a}
                                                    className="text-[11px] font-medium text-white px-2 py-0.5 rounded-full capitalize"
                                                    style={{ background: accentFor(a) }}
                                                >
                                                    {a}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => toggleActive(u)}
                                            data-testid={`user-toggle-${u.email}`}
                                            className={`inline-flex items-center gap-1.5 text-xs font-medium ${u.active ? "text-sage-700" : "text-stone-400"}`}
                                        >
                                            {u.active ? <CircleDot size={13} /> : <Circle size={13} />}
                                            {u.active ? "Active" : "Disabled"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => openEdit(u)}
                                                data-testid={`user-edit-${u.email}`}
                                                className="p-2 rounded-md text-stone-500 hover:text-orchid-700 hover:bg-orchid-700/5"
                                                title="Edit"
                                            >
                                                <Pencil size={15} />
                                            </button>
                                            <button
                                                onClick={() => { setPwUser(u); setPw(""); }}
                                                data-testid={`user-reset-${u.email}`}
                                                className="p-2 rounded-md text-stone-500 hover:text-orchid-700 hover:bg-orchid-700/5"
                                                title="Reset password"
                                            >
                                                <KeyRound size={15} />
                                            </button>
                                            <button
                                                onClick={() => remove(u)}
                                                data-testid={`user-delete-${u.email}`}
                                                className="p-2 rounded-md text-stone-500 hover:text-rose-600 hover:bg-rose-50"
                                                title="Delete"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create / edit modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-form-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="font-heading text-xl text-stone-900 flex items-center gap-2">
                                <UsersIcon size={18} className="text-orchid-700" />
                                {editingId ? "Edit user" : "Add user"}
                            </h2>
                            <button onClick={() => setShowForm(false)} className="text-stone-400 hover:text-stone-700">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">Full name</label>
                                <input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="user-form-name"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-600 focus:ring-2 focus:ring-orchid-700/15"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">Email</label>
                                <input
                                    value={form.email}
                                    disabled={!!editingId}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="user-form-email"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-600 focus:ring-2 focus:ring-orchid-700/15 disabled:bg-stone-50 disabled:text-stone-500"
                                />
                            </div>
                            {!editingId && (
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">Initial password</label>
                                    <input
                                        type="text"
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        placeholder="At least 8 characters"
                                        data-testid="user-form-password"
                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-600 focus:ring-2 focus:ring-orchid-700/15"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-2">Role</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {["member", "owner"].map((r) => (
                                        <button
                                            key={r}
                                            type="button"
                                            data-testid={`user-role-${r}`}
                                            onClick={() => setForm((f) => ({ ...f, role: r, agent_access: r === "owner" ? ["priya", "tara"] : f.agent_access.length ? f.agent_access : ["tara"] }))}
                                            className={`px-3 py-2 rounded-md text-sm border capitalize transition-colors ${form.role === r ? "border-orchid-600 bg-orchid-700/5 text-orchid-700 font-medium" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                                        >
                                            {r}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {form.role === "member" && (
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-2">Which agents can they work on?</label>
                                    <div className="space-y-2">
                                        {ACCESS_PRESETS.map((p) => (
                                            <button
                                                key={p.key}
                                                type="button"
                                                data-testid={`user-access-${p.key}`}
                                                onClick={() => setAccessPreset(p.value)}
                                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm border text-left transition-colors ${presetKey(form.agent_access) === p.key ? "border-orchid-600 bg-orchid-700/5 text-orchid-700 font-medium" : "border-stone-200 text-stone-700 hover:border-stone-300"}`}
                                            >
                                                <span className="flex gap-1">
                                                    {p.value.map((a) => (
                                                        <span key={a} className="h-2.5 w-2.5 rounded-full" style={{ background: accentFor(a) }} />
                                                    ))}
                                                </span>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900">
                                Cancel
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="user-form-save"
                                className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2 rounded-md disabled:opacity-60"
                            >
                                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                                {editingId ? "Save changes" : "Create user"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset password modal */}
            {pwUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-pw-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-heading text-lg text-stone-900 flex items-center gap-2">
                                <KeyRound size={16} className="text-orchid-700" /> Reset password
                            </h2>
                            <button onClick={() => setPwUser(null)} className="text-stone-400 hover:text-stone-700">
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-sm text-stone-600 mb-3">
                            Set a new password for <strong>{pwUser.email}</strong>.
                        </p>
                        <input
                            type="text"
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            placeholder="At least 8 characters"
                            data-testid="user-pw-input"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-600 focus:ring-2 focus:ring-orchid-700/15"
                        />
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setPwUser(null)} className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900">
                                Cancel
                            </button>
                            <button
                                onClick={resetPassword}
                                data-testid="user-pw-save"
                                className="bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2 rounded-md"
                            >
                                Reset password
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
